import { useState, type FormEvent } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { AlertBanner, Button, FormField, Input } from "@lendcord/concord";
import { PasswordInput } from "@/auth/components/PasswordInput";
import { useLoginMutation, type AuthTokenResponse } from "@/auth/api/auth.api";
import { isQueryApiError } from "@/app/store/baseApi";
import { EMAIL_REGEX, PHONE_REGEX, toIndianE164 } from "@/auth/validators";

interface LoginFormProps {
  onSuccess: (result: AuthTokenResponse) => void;
  onForgotPassword: () => void;
}

export function LoginForm({ onSuccess, onForgotPassword }: LoginFormProps) {
  const intl = useIntl();
  const [login] = useLoginMutation();
  const [identifier, setIdentifier] = useState("");
  const [password, setPassword] = useState("");
  const [errors, setErrors] = useState<{ identifier?: string; password?: string }>({});
  const [formError, setFormError] = useState<string | undefined>();
  const [isSubmitting, setIsSubmitting] = useState(false);

  function validate(): boolean {
    const nextErrors: typeof errors = {};
    const trimmedIdentifier = identifier.trim();

    if (!EMAIL_REGEX.test(trimmedIdentifier) && !PHONE_REGEX.test(trimmedIdentifier)) {
      nextErrors.identifier = intl.formatMessage({ id: "auth.loginForm.identifierError" });
    }
    if (!password) {
      nextErrors.password = intl.formatMessage({ id: "auth.loginForm.passwordError" });
    }
    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  }

  async function handleSubmit(event: FormEvent) {
    event.preventDefault();
    setFormError(undefined);
    if (!validate()) return;

    const trimmedIdentifier = identifier.trim();
    const isEmail = EMAIL_REGEX.test(trimmedIdentifier);

    setIsSubmitting(true);
    try {
      const result = await login({
        identifierType: isEmail ? "EMAIL" : "PHONE",
        identifier: isEmail ? trimmedIdentifier : toIndianE164(trimmedIdentifier),
        password,
      }).unwrap();
      onSuccess(result);
    } catch (error) {
      if (isQueryApiError(error) && error.code === "LEND-021") {
        setFormError(intl.formatMessage({ id: "auth.loginForm.rateLimitedError" }));
      } else {
        setFormError(intl.formatMessage({ id: "auth.loginForm.invalidCredentialsError" }));
      }
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <form onSubmit={handleSubmit} noValidate className="flex flex-col gap-5">
      {formError && <AlertBanner status="error">{formError}</AlertBanner>}

      <FormField
        label={intl.formatMessage({ id: "auth.loginForm.identifierLabel" })}
        error={errors.identifier}
        required
      >
        <Input
          value={identifier}
          onChange={(event) => setIdentifier(event.target.value)}
          placeholder={intl.formatMessage({ id: "auth.loginForm.identifierPlaceholder" })}
          autoComplete="username"
        />
      </FormField>

      <FormField
        label={intl.formatMessage({ id: "auth.loginForm.passwordLabel" })}
        error={errors.password}
        required
      >
        <PasswordInput
          value={password}
          onChange={setPassword}
          placeholder={intl.formatMessage({ id: "auth.loginForm.passwordPlaceholder" })}
          autoComplete="current-password"
        />
      </FormField>

      <button
        type="button"
        onClick={onForgotPassword}
        className="-mt-3 self-end text-sm font-medium text-accent-600 hover:underline"
      >
        <FormattedMessage id="auth.loginForm.forgotPasswordLink" />
      </button>

      <Button type="submit" disabled={isSubmitting}>
        {isSubmitting ? (
          <FormattedMessage id="auth.loginForm.submittingButton" />
        ) : (
          <FormattedMessage id="auth.loginForm.submitButton" />
        )}
      </Button>
    </form>
  );
}
